package device

import api.AppBehavior
import api.CampaignType
import api.VisualRotaryMode
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.serialization.Serializable
import ksp.resource.ResClass
import ksp.resource.ResImage
import ksp.resource.ResRoot
import ksp.resource.ResVideo
import org.ssoft.common.tools.EMPTY
import org.ssoft.common.tools.MAX
import org.ssoft.common.tools.MIN
import playlist.ImplPlaylist

@ResRoot("DeviceApps")
@Serializable
data class DevicePlaylist(
    override var deviceId: Long = -1,
    @ResClass(Campaign::class, true, false)
    override var campaigns: MutableList<Campaign> = mutableListOf(),
    @ResClass(SettingsCompany::class, false, false)
    override var settingsCompany: SettingsCompany = SettingsCompany(),
) : ImplPlaylist<DevicePlaylist.Campaign, DevicePlaylist.Visual, DevicePlaylist.SettingsCompany> {

    @Serializable
    data class SettingsCompany(
        override var campaignType: Int = CampaignType.NONE.type,
        override var entityCountDefault: Int = 1,
        override var entityDurationDefault: Long = 10_000,
        @ResImage(false)
        override var defaultVisual: Long = -1L
    ) : ImplPlaylist.ImplSettingsCompany

    @Serializable
    data class Campaign(
        override var campaignId: Long,
        override var campaignGroupId: Long = ((campaignId - 13) + 27),
        override var itemDuration: Long,
        override var itemCount: Int,
        override var nameCampaign: String,
        override var position: Int,
        override var companyId: Long,
        override var start: LocalDate,
        override var end: LocalDate,
        override var isCommercial: Boolean,
        override var isNonCommercial: Boolean,
        override var volume: Int = 0,
        override var excludedDoY: List<Int> = listOf(),
        override var appBehavior: Int = AppBehavior.CAMPAIGN_OVERLAP.mask,
        override var offsets: List<Long> = listOf(),
        override var visualRotaryMode: Int = VisualRotaryMode.DYNAMIC_VISUALS.mask,
        @ResClass(Visual::class, false, false)
        var primary: Visual,
        @ResClass(Visual::class, true, false)
        var alternative: List<Visual>,
        override var apps: List<ImplPlaylist.AppConfig> = listOf(),
        var miniApp: List<MiniApp> = listOf(),
    ) : ImplPlaylist.ImplCampaign<Visual>

    @Serializable
    data class Visual(
        @ResImage(false)
        @ResVideo(false, "ResourceType.VIDEO match data.type")
        override var resourceId: Long,
        override var campaignResId: Long,
        override var campaignVisualId: Long,
        override var duration: Long,
        override var type: Int,
        override var width: Int,
        override var height: Int,
        override var isLand: Boolean,

        @Deprecated("use from conditions", replaceWith = ReplaceWith("conditions.days"))
        override var days: Int,

        @Deprecated("use from conditions", replaceWith = ReplaceWith("conditions.time.start"))
        override var timeStart: LocalTime,

        @Deprecated("use from conditions", replaceWith = ReplaceWith("conditions.time.end"))
        override var timeEnd: LocalTime,

        override var conditions: ImplPlaylist.Conditions
        = ImplPlaylist.Conditions(
            days = days,
            time = ImplPlaylist.Conditions.Time(
                start = timeStart,
                end = timeEnd
            )
        )
    ) : ImplPlaylist.ImplVisual {
        companion object {
            fun empty(resourceId: Long, type: Int): Visual {
                return Visual(
                    resourceId = resourceId,
                    type = type,
                    campaignVisualId = -1,
                    campaignResId = -1,
                    days = -1,
                    duration = 0,
                    timeStart = LocalTime.MIN,
                    timeEnd = LocalTime.MAX,
                    width = 0,
                    height = 0,
                    isLand = true,
                    conditions = ImplPlaylist.Conditions(
                        days = -1,
                        time = ImplPlaylist.Conditions.Time(
                            start = LocalTime.MIN,
                            end = LocalTime.MAX
                        )
                    )
                )
            }
        }
    }

    @Serializable
    data class MiniApp(
        var enabled: Boolean = false,
        var dt: LocalDateTime = LocalDateTime.EMPTY,
        var bg: Long = 0xFFFFFFFFL,
        var fg: Long = 0xFF000000L,
        val labels: Labels = Labels()
    ) {
        companion object;

        @Serializable
        data class Labels(
            val days: String = "Days",
            val hours: String = "Hours",
            val minutes: String = "Minutes",
            val seconds: String = "Seconds",
            val ended: String = "Ended",
        )
    }

    companion object {
        fun empty(
            deviceId: Long,
            durationOfSlot: Long,
            horizontalScreen: Boolean
        ): DevicePlaylist {
            return DevicePlaylist(
                deviceId = deviceId,
                campaigns = mutableListOf(),
                settingsCompany = SettingsCompany(
                    entityDurationDefault = durationOfSlot,
                    defaultVisual = if (horizontalScreen) -1L else -2L
                ),
            )
        }
    }

}


